// EID-103 R7: PanelNode used to compute `align`/`justify` once as plain
// `const`s at component setup, so patching a panel's `position` after mount
// never updated the flex alignment. Extracted here as a pure function so
// PanelNode can wrap it in a createMemo (re-derives whenever props.position
// changes) without duplicating the alignment rules.
export type PanelPosition = {
  top?: unknown;
  bottom?: unknown;
  left?: unknown;
  right?: unknown;
} | null;

export type PanelAlignment = {
  align: 'start' | 'center';
  justify: 'start' | 'center';
};

export const resolvePanelAlignment = (
  position: PanelPosition | undefined,
): PanelAlignment => ({
  align: position?.top || position?.bottom ? 'start' : 'center',
  justify: position?.left || position?.right ? 'start' : 'center',
});

// Panel width/height from the spec are applied verbatim, so a panel larger
// than its container used to spill past the edges with no scroll surface able
// to reach it (modal positioner and .floatingPanel* are fixed/hidden
// overflow). These clamps cap a panel at its containing block minus any
// pinned offsets on the same axis; the panel's own content area scrolls the
// remainder.
export type PanelMaxSize = { maxWidth: string; maxHeight: string };

const toCssLength = (v: unknown): string | null =>
  v == null ? null : typeof v === 'number' ? `${v}px` : String(v);

const axisClamp = (a: unknown, b: unknown): string => {
  const offsets = [toCssLength(a), toCssLength(b)].filter(
    (o): o is string => o !== null,
  );
  return offsets.length ? `calc(100% - ${offsets.join(' - ')})` : '100%';
};

export const resolvePanelMaxSize = (
  position: PanelPosition | undefined,
): PanelMaxSize => ({
  maxWidth: axisClamp(position?.left, position?.right),
  maxHeight: axisClamp(position?.top, position?.bottom),
});

// The max-size clamps above keep the panel frame — and its close button — on
// screen, but panel content nodes are fit-to-container (grid re-measures,
// Vega autosizes), so a clamped frame would silently squash them. This keeps
// the content at its spec size inside the frame's scroll area (the header is
// measured out of the height), so a clamped panel pans to reach everything
// instead. Rendered as a floor (paired with min-width:100%/flex-grow in the
// scroll wrapper): percentage specs resolve below the floor and stay
// fit-to-frame, so panels that fit are unaffected.
export type PanelContentSize = { width?: string; height?: string };

export const resolvePanelContentSize = (
  width: unknown,
  height: unknown,
  headerPx: number,
): PanelContentSize => {
  const w = toCssLength(width);
  const h = toCssLength(height);
  return {
    width: w ?? undefined,
    height: h ? (headerPx ? `calc(${h} - ${headerPx}px)` : h) : undefined,
  };
};
